import { ref } from '@nuxtjs/composition-api';
import { defineStore } from 'pinia';

import { legalDocumentsApiConfig } from '@/config/api/legalDocuments';
import { useClientApi } from '@/plugins/api/lk';
import { getCityOfSale } from '@/shared/composables';
import { MENU_TYPE } from '@/shared/constants';
import {
  decorateApiActions,
  errorByValueMessage,
} from '@/shared/lib';
import type {
  AcceptedDocument,
  AcceptedDocumentV2,
  DocumentDetail,
  DocumentSection,
} from '@/types/LegalDocuments';

export const NO_SECTION_SLUG = 'no-section';

export const useLegalDocumentsStore = defineStore('legalDocuments', () => {
  const acceptedDocuments = ref<Array<AcceptedDocument>>([]);
  const acceptedDocumentsV2 = ref<Array<AcceptedDocumentV2>>([]);
  const documentDetail = ref<DocumentDetail>();
  const documentDetailFrom = ref<string>();
  const documentSections = ref<Array<DocumentSection>>([]);

  /**
   * Метод получение принятых документов
   * @param {Array<string>} slugs - слаги документов
   */
  async function loadAcceptedLegalDocuments (slugs: Array<string>): Promise<void> {
    const url = legalDocumentsApiConfig.accept;
    const { data } = await useClientApi().get<Array<AcceptedDocument>>(url, {
      params: {
        menu_type: MENU_TYPE.LK_BROKER,
        slug: slugs,
      },
    });
    acceptedDocuments.value = data;
  }

  /**
   * Метод получение принятых документов V2
   */
  async function loadAcceptedLegalDocumentsV2 (): Promise<void> {
    const url = legalDocumentsApiConfig.acceptV2;
    const { data } = await useClientApi().get<Array<AcceptedDocumentV2>>(url, {
      params: {
        menu_type: MENU_TYPE.LK_BROKER,
      },
    });
    acceptedDocumentsV2.value = data.filter(Boolean);
  }

  /**
   * Метод принятия юр документов
   * @param {Array<string>} slugs - слаги документов
   */
  async function sendAcceptedLegalDocument (slugs: Array<string>) {
    const url = legalDocumentsApiConfig.accept;

    await useClientApi().post(url, {
      menuType: MENU_TYPE.LK_BROKER,
      slugs,
    });
  }

  async function getDocumentsList (): Promise<void> {
    const cityOfSale = getCityOfSale();
    const url = legalDocumentsApiConfig.list;

    const {
      data,
    } = await useClientApi().get<Array<DocumentSection>>(url, {
      params: {
        city: cityOfSale?.slug,
        menu_type: MENU_TYPE.LK_BROKER,
      },
    });

    documentSections.value = data;
  }

  async function getDocumentDetail (slug: string, version?: number, agencyName?: string): Promise<DocumentDetail> {
    let citySlug = getCityOfSale()?.slug;
    if (!citySlug) {
      citySlug = 'tmn'; // В деталке брокера не важно какой город для не авторизированного юзера

      // if (!documentSections.value.length) {
      //   await getDocumentsList();
      // }
      // схватим первый попавшийся город, от него и будем дальше плясать
      // citySlug = documentSections.value
      //   .find(section => section.slug === NO_SECTION_SLUG)?.documents // секции может не быть
      //   .at(0)?.documentCities
      //   .filter(value => value)
      //   .at(0);
    }

    if (!citySlug) {
      throw new Error(errorByValueMessage({ citySlug }));
    }

    documentDetail.value = undefined;
    const url = legalDocumentsApiConfig.detail;
    const {
      data,
    } = await useClientApi().get<DocumentDetail>(url, {
      params: {
        city: citySlug,
        slug,
        agency_name: agencyName,
        version,
      },
    });

    documentDetail.value = data;

    return data;
  }

  const decoratedApiActions = decorateApiActions({
    loadAcceptedLegalDocuments,
    loadAcceptedLegalDocumentsV2,
    getDocumentsList,
  }, 'store/documents.ts');

  const rethrow = decorateApiActions({
    sendAcceptedLegalDocument,
    getDocumentDetail,
  }, 'store/documents.ts', true);

  return {
    acceptedDocuments,
    acceptedDocumentsV2,
    ...decoratedApiActions,
    ...rethrow,
    documentDetail,
    documentDetailFrom,
    documentSections,
  };
});
